import { apiClient } from '@littlebird/auth-client';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import { queryClient } from '@/query/client';

import {
  WIKI_PROVIDERS,
  type WikiAccount,
  type WikiFact,
  type WikiLittlebirdAccount,
  type WikiLookupRun,
  type WikiPerson,
  type WikiPersonDetail,
} from './people-wiki.types';

const identitySchema = z.object({
  id: z.string(),
  provider: z.string(),
  stable_id: z.string(),
  display_name: z.string(),
  username: z.string(),
  profile_url: z.string().default(''),
  // Older backends omit `source`, and unknown values must not be trusted as synced.
  source: z.preprocess(
    (value) => value ?? 'sync',
    z.enum(['sync', 'web', 'user', 'agent']).catch('web')
  ),
  // Where the user stands with a Littlebird account. Older backends omit it, and a state this app does not know
  // reads as none.
  connection: z
    .enum(['invited', 'requested', 'incoming', 'connected', ''])
    .catch(''),
  // When a request or invite with no answer yet was sent, or came in. Older backends omit it.
  sent_at: z.string().nullable().default(null),
  // When a request or invite with no answer yet can be sent again. Older backends omit it.
  resend_at: z.string().nullable().default(null),
  // The address belongs to an active teammate. Older backends omit it.
  is_teammate: z.boolean().default(false),
  // The Littlebird account can receive Hive Mind messages now. Older backends omit it.
  hive_mind: z.boolean().default(false),
  // The Littlebird user id of an account the user can message now. Older backends omit it.
  user_id: z.number().nullable().default(null),
});

const personPageSchema = z.object({
  id: z.string(),
  // Older backends omit `source`.
  source: z.preprocess(
    (value) => value ?? 'sync',
    z.enum(['sync', 'user', 'agent']).catch('sync')
  ),
  // The user's own page. Older backends omit it.
  is_self: z.boolean().default(false),
  name: z.string(),
  emails: z.array(z.string()),
  aliases: z.array(z.string()),
  avatar_url: z.string().nullable(),
  is_trusted: z.boolean().default(false),
  identities: z.array(identitySchema),
});

const factSchema = z.object({
  id: z.string(),
  source: z.enum(['web', 'agent', 'user']),
  kind: z
    .enum([
      'summary',
      'role',
      'education',
      'project',
      'publication',
      'writing',
      'location',
      'other',
    ])
    .catch('other'),
  confidence: z.enum(['high', 'medium', 'low', '']).catch(''),
  text: z.string(),
  citations: z.array(
    z.object({ url: z.string(), title: z.string(), excerpt: z.string() })
  ),
  created_at: z.string(),
});

const lookupRunSchema = z.object({
  id: z.string(),
  engine: z.string(),
  status: z.enum(['running', 'matched', 'no_match', 'failed']),
  created_at: z.string(),
  finished_at: z.string().nullable(),
  identity_confidence: z.enum(['high', 'medium', 'low', '']),
  identity_note: z.string(),
  matched_on: z.array(z.string()),
  error: z.string(),
});

export const personDetailSchema = personPageSchema.extend({
  // The page's own other names. Older backends omit it, so every name is then read-only.
  own_aliases: z.array(z.string()).default([]),
  avatar_urls: z.array(z.string()),
  user_notes: z.string(),
  agent_notes: z.string(),
  facts: z.array(factSchema),
  lookup: lookupRunSchema.nullable(),
});

type Identity = z.infer<typeof identitySchema>;
type PersonPage = z.infer<typeof personPageSchema>;
type PersonFact = z.infer<typeof factSchema>;
type LookupRun = z.infer<typeof lookupRunSchema>;
type PersonDetail = z.infer<typeof personDetailSchema>;

const toSlackDeepLink = (stableId: string): string | null => {
  const [teamId, userId] = stableId.split(':');
  return teamId && userId
    ? `slack://user?team=${encodeURIComponent(teamId)}&id=${encodeURIComponent(userId)}`
    : null;
};

/** Who added an account by hand, or `fallback` for an account that an app or a web lookup found. */
const addedByDetail = (
  source: Identity['source'],
  fallback: string
): string => {
  switch (source) {
    case 'user':
      return 'Added by you';
    case 'agent':
      return 'Added by Littlebird';
    case 'sync':
    case 'web':
      return fallback;
  }
};

/** What the badge of a Littlebird account says, or null for an account without a badge. A connection that is only
 * pending shows in the connection controls instead. */
const littlebirdBadgeDetail = (
  identity: Identity,
  isSelf: boolean
): string | null => {
  if (identity.is_teammate) {
    return `Littlebird teammate · ${identity.stable_id}`;
  }
  if (isSelf) {
    return `Your Littlebird account · ${identity.stable_id}`;
  }
  return identity.connection === 'connected'
    ? `Connected on Littlebird · ${identity.stable_id}`
    : null;
};

const toWikiAccount = (
  identity: Identity,
  isSelf: boolean
): WikiAccount | null => {
  switch (identity.provider) {
    case WIKI_PROVIDERS.slack:
      return {
        id: identity.id,
        provider: WIKI_PROVIDERS.slack,
        label: identity.username
          ? `@${identity.username}`
          : identity.display_name,
        detail: 'Open in Slack',
        href: toSlackDeepLink(identity.stable_id),
        source: identity.source,
      };
    case WIKI_PROVIDERS.gmail:
      return {
        id: identity.id,
        provider: WIKI_PROVIDERS.gmail,
        label: identity.stable_id,
        detail: 'Emailed in Gmail',
        href: `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(identity.stable_id)}`,
        source: identity.source,
      };
    case WIKI_PROVIDERS.littlebird: {
      const detail = littlebirdBadgeDetail(identity, isSelf);
      return detail === null
        ? null
        : {
            id: identity.id,
            provider: WIKI_PROVIDERS.littlebird,
            label: 'Littlebird',
            detail,
            href: null,
            source: identity.source,
          };
    }
    case WIKI_PROVIDERS.email:
      return {
        id: identity.id,
        provider: WIKI_PROVIDERS.email,
        label: identity.stable_id,
        detail: addedByDetail(identity.source, 'Email address'),
        href: `mailto:${identity.stable_id}`,
        source: identity.source,
      };
    case WIKI_PROVIDERS.phone:
      return {
        id: identity.id,
        provider: WIKI_PROVIDERS.phone,
        label: identity.stable_id,
        detail: addedByDetail(identity.source, 'Phone number'),
        // The app opens only some link types outside itself, and `tel:` is not one, so a number is plain text
        href: null,
        source: identity.source,
      };
    case WIKI_PROVIDERS.linkedin:
    case WIKI_PROVIDERS.github:
    case WIKI_PROVIDERS.x:
    case WIKI_PROVIDERS.calendly:
    case WIKI_PROVIDERS.website:
      return {
        id: identity.id,
        provider: identity.provider,
        label:
          identity.provider === WIKI_PROVIDERS.website
            ? identity.stable_id
            : `@${identity.username || identity.stable_id}`,
        detail: addedByDetail(identity.source, 'Found on the web'),
        href: identity.profile_url || null,
        source: identity.source,
      };
    default:
      return null;
  }
};

const toLittlebirdAccount = (identity: Identity): WikiLittlebirdAccount => ({
  accountId: identity.id,
  email: identity.stable_id,
  connection: identity.connection,
  sentAt: identity.sent_at,
  resendAt: identity.resend_at,
  isTeammate: identity.is_teammate,
  hiveMind: identity.hive_mind,
  userId: identity.user_id,
});

// A request that came in from someone who is not a teammate
const isIncomingRequest = (identity: Identity): boolean =>
  identity.provider === WIKI_PROVIDERS.littlebird &&
  identity.connection === 'incoming' &&
  !identity.is_teammate;

const toWikiPerson = (person: PersonPage): WikiPerson => {
  const [primaryEmail = null, ...otherEmails] = person.emails;
  const littlebirdAccounts = person.identities
    .filter(({ provider }) => provider === WIKI_PROVIDERS.littlebird)
    .map(toLittlebirdAccount);
  return {
    id: person.id,
    source: person.source,
    isSelf: person.is_self,
    name: person.name,
    avatarUrl: person.avatar_url,
    primaryEmail,
    otherEmails,
    aliases: person.aliases,
    accounts: person.identities.flatMap(
      (identity) => toWikiAccount(identity, person.is_self) ?? []
    ),
    littlebirdAccounts,
    // The stand-in `hivemind` identity older apps read is not an account, so only Littlebird accounts count
    isOnHiveMind: littlebirdAccounts.some(({ hiveMind }) => hiveMind),
    isTrusted: person.is_trusted,
    isTeammate: littlebirdAccounts.some(({ isTeammate }) => isTeammate),
    isRequestOnly:
      person.identities.length > 0 &&
      person.identities.every(isIncomingRequest),
  };
};

const toWikiFact = (fact: PersonFact): WikiFact => ({
  id: fact.id,
  source: fact.source,
  text: fact.text,
  kind: fact.kind,
  confidence: fact.confidence,
  citations: fact.citations,
  createdAt: fact.created_at,
});

const toWikiLookupRun = (run: LookupRun): WikiLookupRun => ({
  id: run.id,
  engine: run.engine,
  status: run.status,
  startedAt: run.created_at,
  finishedAt: run.finished_at,
  identityConfidence: run.identity_confidence,
  identityNote: run.identity_note,
  matchedOn: run.matched_on,
  error: run.error,
});

export const toWikiPersonDetail = (detail: PersonDetail): WikiPersonDetail => ({
  ...toWikiPerson(detail),
  ownAliases: detail.own_aliases,
  avatarUrls: detail.avatar_urls,
  userNotes: detail.user_notes,
  agentNotes: detail.agent_notes,
  facts: detail.facts.map(toWikiFact),
  lookup: detail.lookup && toWikiLookupRun(detail.lookup),
});

export const PEOPLE_STALE_TIME_MS = 5 * 60_000;

export const parseWikiPeople = (response: unknown): WikiPerson[] =>
  z.array(personPageSchema).parse(response).map(toWikiPerson);

/** Trusted people first, then the most recently seen, up to the server's limit of 500. Shared by the Wiki page,
 * the @ picker and the chips in sent messages. */
export const peopleWikiQueryOptions = () =>
  queryOptions({
    queryKey: ['people-wiki', 'people'],
    staleTime: PEOPLE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    queryFn: async ({ signal }): Promise<WikiPerson[]> =>
      parseWikiPeople(await apiClient.get('/people-wiki/people', { signal })),
  });

export const usePeopleWikiQuery = ({
  enabled = true,
}: { enabled?: boolean } = {}) =>
  useQuery({ ...peopleWikiQueryOptions(), enabled }, queryClient);

export const personQueryOptions = (personId: string) =>
  queryOptions({
    queryKey: ['people-wiki', 'people', personId],
    staleTime: PEOPLE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    retry: false,
    queryFn: async ({ signal }): Promise<WikiPersonDetail> => {
      const response = await apiClient.get(`/people-wiki/people/${personId}`, {
        signal,
      });
      return toWikiPersonDetail(personDetailSchema.parse(response));
    },
  });

/** One write at a time per person, notes, edits and connections alike, so a later response cannot be overwritten by an
 * earlier one. */
export const personWriteScope = (personId: string) => ({
  id: `people-wiki-person-${personId}`,
});

/** The list key is a prefix of every person key, so this refreshes the Wiki rows and any open person page. */
export const invalidatePeople = () =>
  queryClient.invalidateQueries({ queryKey: ['people-wiki', 'people'] });

/** Only the list, for a write that has already put the server's answer in the cache of the page it changed. */
export const invalidatePeopleList = () =>
  queryClient.invalidateQueries({
    queryKey: peopleWikiQueryOptions().queryKey,
    exact: true,
  });

export const usePersonQuery = (personId: string) =>
  useQuery(personQueryOptions(personId), queryClient);

export const useOpenPerson = () => {
  const navigate = useNavigate();
  return (personId: string, { rename = false } = {}) =>
    navigate({
      to: '/people-wiki/$personId',
      params: { personId },
      search: rename ? { rename } : {},
    });
};
